import { useEffect } from "react";

import { WithStringValues } from "common/utils/types";

import Results from "client/common/components/results/Results";
import Body from "client/common/components/layouts/Body";
import { ProductListSource } from "client/analytics/types";

import { useProductFilterOptionState, useProductSearchQuery } from "../hooks";
import { PRODUCT_SEARCH_FILTER_SCOPE } from "../hooks/constants";
import { ProductSearchSearch } from "../types";
import ProductCardList from "./ProductCardList";
import ProductCardListHeader from "./ProductCardListHeader";
import { getProductFiltersFromSearch } from "../utils";

import styles from "../styles/ProductSearch.module.less";

type ProductSearchProps = {
  search: WithStringValues<ProductSearchSearch>;
  onUpdateSearch: (search: Partial<ProductSearchSearch>) => void;
};

const ProductSearch = (props: ProductSearchProps) => {
  const { search, onUpdateSearch } = props;

  const { data: products, isLoading, error, isRefetching } = useProductSearchQuery(search);

  const { data: filterOptions } = useProductFilterOptionState(PRODUCT_SEARCH_FILTER_SCOPE);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [search.page]);

  if (isLoading) {
    return <Results.Loading description="Loading products" />;
  }

  if (error || !products) {
    return <Results.FetchError error={error} entityName="Product" />;
  }

  const filters = getProductFiltersFromSearch(search);

  return (
    <Body>
      <div className={styles.container}>
        <ProductCardListHeader
          paginator={products.paginator}
          isRefetching={isRefetching}
          entityName="result"
          options={filterOptions?.filters}
          filters={filters}
          onChange={onUpdateSearch}
        />
        {products.results.length ? (
          <ProductCardList
            results={products.results}
            listSource={ProductListSource.SearchResults}
            searchTerm={search.q}
            paginator={products.paginator}
            onPageChange={(page) => onUpdateSearch({ page })}
            entityName="result"
          />
        ) : (
          <Results.None description={`Could not find any results for "${search.q}"`} />
        )}
      </div>
    </Body>
  );
};

export default ProductSearch;
